(() => {
  const BUTTON_SELECTOR = '.shopflo-wishlist-button';

  function setWishlistState(button, isWishlisted) {
    const title = button.dataset.productTitle || 'Product';

    button.setAttribute('aria-pressed', isWishlisted ? 'true' : 'false');
    button.setAttribute(
      'aria-label',
      isWishlisted
        ? `Remove ${title} from wishlist`
        : `Add ${title} to wishlist`
    );
  }

  function waitForShopflo(timeout = 10000) {
    return new Promise((resolve, reject) => {
      const startedAt = Date.now();

      const check = () => {
        const sdk = window.Shopflo;

        if (
          sdk &&
          typeof sdk.addToWishlist === 'function' &&
          typeof sdk.removeFromWishlist === 'function'
        ) {
          resolve(sdk);
          return;
        }

        if (Date.now() - startedAt >= timeout) {
          reject(new Error('Shopflo Wishlist SDK did not become available.'));
          return;
        }

        setTimeout(check, 100);
      };

      check();
    });
  }

  async function getAllWishlistedProductIds() {
    const sdk = await waitForShopflo();

    if (typeof sdk.getWishlistItems !== 'function') {
      return new Set();
    }

    const ids = new Set();
    const limit = 100;
    let offset = 0;

    for (let page = 0; page < 20; page += 1) {
      const response = await sdk.getWishlistItems({ limit, offset });

      if (!response || response.success !== true) {
        throw new Error(response?.error || 'Unable to fetch wishlist.');
      }

      const wishlists = response?.data?.wishlists || [];

      wishlists.forEach((item) => {
        if (item?.product_id != null) {
          ids.add(String(item.product_id));
        }
      });

      const totalCount = Number(response?.data?.total_count || 0);
      offset += wishlists.length;

      if (wishlists.length === 0 || offset >= totalCount) {
        break;
      }
    }

    return ids;
  }

  async function syncWishlistButtons() {
    try {
      const ids = await getAllWishlistedProductIds();

      document.querySelectorAll(BUTTON_SELECTOR).forEach((button) => {
        setWishlistState(
          button,
          ids.has(String(button.dataset.productId || ''))
        );
      });
    } catch (error) {
      // getWishlistItems requires the user to be logged in to Shopflo.
      // Keep logged-out collection pages working normally.
      console.debug('[Shopflo wishlist] Initial sync skipped:', error);
    }
  }

  document.addEventListener('click', async (event) => {
    const button = event.target.closest?.(BUTTON_SELECTOR);
    if (!button) return;

    event.preventDefault();
    event.stopPropagation();

    if (button.classList.contains('is-loading')) return;

    const productId = String(button.dataset.productId || '');
    if (!productId) return;

    const wasWishlisted = button.getAttribute('aria-pressed') === 'true';

    button.classList.add('is-loading');
    button.setAttribute('aria-busy', 'true');

    try {
      const sdk = await waitForShopflo();

      const response = wasWishlisted
        ? await sdk.removeFromWishlist({ productId })
        : await sdk.addToWishlist({ productId });

      if (!response || response.success !== true) {
        throw new Error(response?.error || 'Wishlist update failed.');
      }

      setWishlistState(button, !wasWishlisted);

      document.dispatchEvent(
        new CustomEvent('shopflo:wishlist:changed', {
          detail: {
            productId,
            wishlisted: !wasWishlisted,
            response
          }
        })
      );
    } catch (error) {
      console.error('[Shopflo wishlist]', error);
    } finally {
      button.classList.remove('is-loading');
      button.removeAttribute('aria-busy');
    }
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', syncWishlistButtons, { once: true });
  } else {
    syncWishlistButtons();
  }

  document.addEventListener('shopify:section:load', syncWishlistButtons);
})();
